import { useCallback, useState } from "react";

import type { ToastKind } from "./toast";

export interface Writer {
  /** True while a write is in flight, so controls can stop inviting a second. */
  busy: boolean;
  toast: { kind: ToastKind; message: string } | null;
  dismiss: () => void;
  /** Show a failure without having run a write — a read that did not land. */
  fail: (message: string) => void;
  /**
   * Run one write, re-read what it changed, then say what happened.
   *
   * **Resolves `true` only if the write landed.** Callers that close a panel
   * must wait for it: closing on the next line, as they all used to, discards
   * everything typed the moment a save is refused.
   *
   * **`done` may read the write's own result** (STORY-122) — a save that
   * succeeds can still carry a signal worth surfacing, like a phone number
   * colliding with somebody else's, and that only exists on the response the
   * write itself returns.
   */
  run: (
    write: () => Promise<unknown>,
    reload: () => Promise<void>,
    done: string | ((result: unknown) => string),
  ) => Promise<boolean>;
}

/**
 * One place every settings write goes through, so feedback comes from one
 * place and each screen does not invent its own.
 *
 * **The server's own message is shown verbatim on failure.** A retire refused
 * under decision S1 comes back as "This chair has 3 appointments still to
 * come", which is the sentence the front desk needs; paraphrasing it here would
 * lose the count the whole rule exists to report.
 */
export function useWriter(): Writer {
  const [toast, setToast] = useState<{ kind: ToastKind; message: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const dismiss = useCallback(() => setToast(null), []);

  const fail = useCallback((message: string) => setToast({ kind: "error", message }), []);

  /**
   * **Returns whether the write actually landed** (client, 2026-09-06).
   *
   * It used to return `void`, so every caller closed its form on the line after
   * calling this — synchronously, before the request had even been sent. A
   * refused save therefore *closed the panel and threw away everything typed*,
   * leaving a toast about a form that was no longer on screen. His words: "when
   * i get an error ... the section is auto closed".
   *
   * A promise of a boolean rather than a rethrow, because the caller's job here
   * is a UI decision — keep the panel open, mark the field — and not error
   * handling. The toast is still raised from here, so no caller has to remember
   * to.
   */
  const run = useCallback(
    async (
      write: () => Promise<unknown>,
      reload: () => Promise<void>,
      done: string | ((result: unknown) => string),
    ): Promise<boolean> => {
      setBusy(true);
      setToast(null);

      try {
        // Awaited rather than chained so the reload's own result cannot be
        // mistaken for the write's — `.then(reload)` used to hand the write's
        // value to `reload` as its `signal`, which reached `fetch` as a Branch.
        const result = await write();
        await reload();
        setToast({ kind: "done", message: typeof done === "function" ? done(result) : done });
        return true;
      } catch (cause: unknown) {
        setToast({ kind: "error", message: describe(cause, "That change did not go through.") });
        return false;
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  return { busy, toast, dismiss, fail, run };
}

/** The server's message where there is one, a plain sentence where there is not. */
export function describe(cause: unknown, fallback: string): string {
  return cause instanceof Error && cause.message !== "" ? cause.message : fallback;
}
